import { tr } from "@/lib/translations";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sun, Cloud, CloudSun, CloudRain, CloudFog, CloudLightning, Snowflake, Wind } from "lucide-react";

// WMO weather_code → Icon + Text
const describe = (code, lang) => {
  const t = {
    sun: { de: "Sonnig", en: "Sunny", es: "Soleado" }, partly: { de: "Leicht bewölkt", en: "Partly cloudy", es: "Parcialmente nublado" },
    cloud: { de: "Bewölkt", en: "Cloudy", es: "Nublado" }, fog: { de: "Nebel", en: "Fog", es: "Niebla" }, rain: { de: "Regen", en: "Rain", es: "Lluvia" },
    snow: { de: "Schnee", en: "Snow", es: "Nieve" }, storm: { de: "Gewitter", en: "Thunderstorm", es: "Tormenta" },
  };
  if (code === 0) return [Sun, t.sun[lang]];
  if (code <= 2) return [CloudSun, t.partly[lang]];
  if (code === 3) return [Cloud, t.cloud[lang]];
  if (code <= 48) return [CloudFog, t.fog[lang]];
  if (code <= 67 || (code >= 80 && code <= 82)) return [CloudRain, t.rain[lang]];
  if (code <= 77 || code === 85 || code === 86) return [Snowflake, t.snow[lang]];
  return [CloudLightning, t.storm[lang]];
};
const LABEL = { de: "Aktuell", en: "Now", es: "Ahora" };

const NOW_IN = { de: "Aktuell in", en: "Now in", es: "Ahora en" };

// Kompaktes Live-Temperatur-Badge für die Objektseite (Koordinaten des Objekts, Fallback: 1. Insel-Station)
export const WeatherBadge = ({ lat, lng, island, location, lang = "de" }) => {
  const [w, setW] = useState(null);
  useEffect(() => {
    setW(null);
    const req = lat != null && lng != null ? api.get("/weather/point", { params: { lat, lng } }) : island ? api.get(`/weather/${island}`).then((r) => ({ data: (r.data.stations || [])[0] })) : null;
    req ? req.then((r) => setW(r.data?.temp != null ? r.data : false)).catch(() => setW(false)) : setW(false);
  }, [lat, lng, island]);
  if (w === null) return <span className="inline-flex w-14 h-[22px]" aria-hidden="true" />; // Platzhalter in Badge-Größe → kein Layout-Sprung, wenn die Temperatur eintrifft
  if (!w) return null;
  const [Icon, text] = describe(w.code, lang);
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#eaf2ff] text-[#1d4f91] px-2.5 py-0.5 text-xs font-medium" title={`${NOW_IN[lang]} ${location}: ${text} · ${w.min}° / ${w.max}°`} data-testid="property-weather-badge">
      <Icon className="w-3.5 h-3.5" /> {w.temp}°C
    </span>
  );
};

// Live-Temperatur-Blöcke (z.B. Teneriffa Nord / Süd), Daten von Open-Meteo via Backend-Cache
export const WeatherBlocks = ({ island, lang = "de" }) => {
  const [w, setW] = useState(null);
  useEffect(() => { setW(null); api.get(`/weather/${island}`).then((r) => setW(r.data)).catch(() => setW({ stations: [] })); }, [island]);
  if (!w || !Array.isArray(w.stations) || !w.stations.length) return null;
  return w.stations.map((s) => {
    const [Icon, text] = describe(s.code, lang);
    return (
      <div key={s.key} className="rounded-2xl bg-[#eaf2ff] p-4" data-testid={`weather-${s.key}`}>
        <div className="text-[10px] uppercase tracking-widest text-[#1d4f91]">{LABEL[lang]} · {tr(s.name, lang)}</div>
        <div className="flex items-center gap-2 mt-1">
          <Icon className="w-6 h-6 text-[#1d4f91]" />
          <span className="text-2xl font-semibold text-stone-800" data-testid={`weather-${s.key}-temp`}>{s.temp}°C</span>
        </div>
        <div className="text-xs text-stone-600 mt-1 flex items-center gap-2">{text} · {s.min}° / {s.max}° <span className="inline-flex items-center gap-0.5"><Wind className="w-3 h-3" />{s.wind} km/h</span></div>
      </div>
    );
  });
};
